import { useEffect, useState } from "react";
import { Camera, Loader2, Sparkles, RefreshCw } from "lucide-react";
import { adminBehindTheLens, adminKitOptions } from "@/lib/api";
import { toast } from "sonner";

// Friendly labels for each claim key (mirrors backend CLAIM_KEYS order)
const CLAIM_LABELS = {
  drone_shot: "Drone shot",
  aerial_perspective: "Aerial / top-down",
  long_exposure: "Long exposure",
  light_trails: "Light trails",
  motion_blur: "Motion blur",
  shallow_dof: "Shallow depth of field",
  tripod_used: "Tripod used",
  night_works: "Night works",
  golden_hour: "Golden hour",
  time_of_day: "Time of day",
};

// Show a claim if its value is meaningful (true OR a non-default string)
const isActive = (key, value) => {
  if (value == null) return false;
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    const v = value.toLowerCase();
    if (key === "time_of_day") return !!v;
    return !!v;
  }
  return false;
};

const formatClaimValue = (key, value) => {
  if (typeof value === "boolean") return null;
  if (typeof value === "string") return value;
  return null;
};

/**
 * Behind-the-Lens editor for the admin edit page.
 *
 * Props:
 *  itemId - gallery item id (number) needed to call the AI endpoint
 *  technical - current technical sub-doc (incl. kit_used, behind_the_lens, behind_the_lens_claims)
 *  onChange - (newTechnical) => void  - parent merges into form state, saved by 'Save changes'
 */
export default function BehindTheLensEditor({ itemId, technical, onChange }) {
  const t = technical || {};
  const [kit, setKit] = useState({ cameras: [], drones: [] });
  const [generating, setGenerating] = useState(false);

  // Local claim state - what's currently ticked. Untick = "this is wrong, remove it on next rewrite".
  const initialTicks = () => {
    const c = t.behind_the_lens_claims || {};
    const ticks = {};
    for (const k of Object.keys(CLAIM_LABELS)) ticks[k] = isActive(k, c[k]);
    return ticks;
  };
  const [ticks, setTicks] = useState(initialTicks);
  const [claimsCache, setClaimsCache] = useState(t.behind_the_lens_claims || null);

  useEffect(() => {
    adminKitOptions()
      .then((k) => setKit({ cameras: k.cameras || [], drones: k.drones || [] }))
      .catch(() => {});
  }, []);

  const update = (patch) => onChange({ ...t, ...patch });

  const runGenerate = async ({ withCorrections = false } = {}) => {
    setGenerating(true);
    try {
      let corrections = [];
      if (withCorrections && claimsCache) {
        for (const k of Object.keys(CLAIM_LABELS)) {
          if (isActive(k, claimsCache[k]) && !ticks[k]) corrections.push(k);
        }
      }
      const data = await adminBehindTheLens(itemId, {
        kit_used: t.kit_used || null,
        corrections,
      });

      // Merge tech_suggestions into the parent technical state - only fill missing fields
      const BLANK = ["", "Unknown", "Not available", "Not Available", "unknown"];
      const isBlank = (v) => v == null || (typeof v === "string" && (v.trim() === "" || BLANK.includes(v.trim())));
      const techSugg = data.tech_suggestions || {};
      const currentAi = new Set(t.ai_suggested || []);
      const techPatch = {};
      for (const [k, v] of Object.entries(techSugg)) {
        if (isBlank(t[k])) {
          techPatch[k] = v;
          currentAi.add(k);
        }
      }
      // BTL prose itself is AI
      if (data.behind_the_lens) currentAi.add("behind_the_lens");

      update({
        kit_used: data.kit_used || t.kit_used || null,
        behind_the_lens: data.behind_the_lens || "",
        behind_the_lens_claims: data.claims || null,
        ai_suggested: Array.from(currentAi),
        ...techPatch,
      });
      setClaimsCache(data.claims || null);
      const newTicks = {};
      for (const k of Object.keys(CLAIM_LABELS)) newTicks[k] = isActive(k, (data.claims || {})[k]);
      setTicks(newTicks);
      const filled = Object.keys(techPatch).length;
      toast.success(
        withCorrections
          ? `Rewritten with corrections.${filled ? ` ${filled} tech field${filled > 1 ? "s" : ""} suggested.` : ""}`
          : `Behind-the-Lens ready.${filled ? ` ${filled} tech field${filled > 1 ? "s" : ""} suggested.` : ""}`
      );
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Behind-the-Lens generation failed.");
    } finally {
      setGenerating(false);
    }
  };

  // Untick deltas - used to enable the Rewrite button
  const hasUntickedClaims =
    claimsCache &&
    Object.keys(CLAIM_LABELS).some((k) => isActive(k, claimsCache[k]) && !ticks[k]);

  const wordCount = (t.behind_the_lens || "").trim()
    ? (t.behind_the_lens || "").trim().split(/\s+/).length
    : 0;

  return (
    <div className="bg-[#0f0f0f] border border-[#1a1a1a] p-5 mt-6" data-testid="btl-panel">
      <div className="flex items-center gap-2 text-[10px] tracking-[0.32em] uppercase text-[#4A6D8C] mb-4">
        <Camera size={12} /> Behind the Lens
      </div>

      {/* Kit dropdown */}
      <div className="grid grid-cols-[150px_1fr] gap-3 items-center border-b border-white/[0.04] py-2">
        <div className="text-[10px] tracking-[0.22em] uppercase text-[#737373]">Kit used</div>
        <select
          value={t.kit_used || ""}
          onChange={(e) => update({ kit_used: e.target.value || null })}
          className="hl-input py-1.5 text-[12px]"
          data-testid="btl-kit-select"
        >
          <option value="">Select camera or drone</option>
          <optgroup label="Cameras">
            {kit.cameras.map((c) => <option key={c} value={c}>{c}</option>)}
          </optgroup>
          <optgroup label="Drones">
            {kit.drones.map((d) => <option key={d} value={d}>{d}</option>)}
          </optgroup>
          <option value="Other">Other / Unknown</option>
        </select>
      </div>

      {/* Prose textarea */}
      <div className="mt-3">
        <div className="flex items-baseline justify-between mb-1">
          <div className="text-[10px] tracking-[0.22em] uppercase text-[#737373]">
            Behind the Lens prose
          </div>
          {wordCount > 0 && (
            <div className="text-[10px] text-[#525252]">{wordCount} words</div>
          )}
        </div>
        <textarea
          value={t.behind_the_lens || ""}
          onChange={(e) => update({ behind_the_lens: e.target.value })}
          placeholder="Click Generate below. The AI will assert the settings used, your job is to untick anything wrong."
          rows={2}
          className="hl-input py-2 text-[12px]"
          data-testid="btl-prose"
        />
      </div>

      {/* Claim checkboxes */}
      {claimsCache && (
        <div className="mt-3">
          <div className="text-[10px] tracking-[0.22em] uppercase text-[#737373] mb-2">
            AI assumed - untick any that are wrong
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5" data-testid="btl-claims">
            {Object.keys(CLAIM_LABELS).map((k) => {
              const label = CLAIM_LABELS[k];
              const aiThoughtTrue = isActive(k, claimsCache[k]);
              if (!aiThoughtTrue) return null;
              // Kit-aware filters: drone shots never have tripod/handheld; ground cameras are never aerial
              const isDrone = !!t.kit_used && /^DJI /i.test(t.kit_used);
              const isGroundCamera = !!t.kit_used && !/^DJI /i.test(t.kit_used) && t.kit_used !== "Other";
              if (isDrone && (k === "tripod_used")) return null;
              if (isGroundCamera && (k === "drone_shot" || k === "aerial_perspective")) return null;
              const value = formatClaimValue(k, claimsCache[k]);
              return (
                <label
                  key={k}
                  className="inline-flex items-center gap-2 text-[12px] text-[#c9d1d9] cursor-pointer"
                  data-testid={`btl-claim-${k}`}
                >
                  <input
                    type="checkbox"
                    checked={!!ticks[k]}
                    onChange={(e) => setTicks((p) => ({ ...p, [k]: e.target.checked }))}
                    className="w-4 h-4 accent-[#4A6D8C]"
                  />
                  <span className={ticks[k] ? "" : "line-through text-[#525252]"}>
                    {label}{value ? `: ${value}` : ""}
                  </span>
                </label>
              );
            })}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-2 mt-4">
        <button
          type="button"
          onClick={() => runGenerate({ withCorrections: false })}
          disabled={generating}
          data-testid="btl-generate"
          className="hl-btn-primary inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {generating ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
          {claimsCache ? "Regenerate" : "Generate"}
        </button>
        {claimsCache && (
          <button
            type="button"
            onClick={() => runGenerate({ withCorrections: true })}
            disabled={generating || !hasUntickedClaims}
            data-testid="btl-rewrite"
            className="hl-btn-secondary inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <RefreshCw size={14} />
            Rewrite with corrections
          </button>
        )}
      </div>

      <div className="text-[11px] text-[#525252] mt-3 leading-relaxed">
        Numbers (aperture, shutter, ND) are <em>AI assertions</em> from visible cues, not from EXIF. Edit the prose directly or untick wrong claims and rewrite.
      </div>
    </div>
  );
}
